import { KeyIcon, UserAddIcon } from "@xola/icons";
import { Button } from "@xola/ui-kit";
import React, { useState } from "react";
import { ApiKeyUser, Delegate } from "types";
import { ApiKeyItem } from "./ApiKeyItem";
import { DelegateItem } from "./DelegateItem";
import { filterApiKeysByFilters, filterDelegatesByFilters, filterRealUsers } from "./DelegateList.helpers";
import { DelegateListFilter, DelegateListFilterValues } from "./DelegateListFilter";
import { DelegateModal, ModalMode } from "./DelegateModal";
import { SessionTimeoutSetting } from "./SessionTimeout";
import { HeaderToolBarActions } from "@/components/Common/HeaderToolbarActions";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { Loader } from "@/components/Loader";
import { useSeller } from "@/hooks/useAccount";
import { useCollection } from "@/hooks/useCollection";
import { sortBy } from "@/utils/locale";

const defaultFilters: DelegateListFilterValues = {
    permissions: { all: true, selected: [] },
    status: "all",
};

const sectionClassNames = {
    content: "min-w-0 !max-w-full lg:w-0 lg:flex-1",
    title: "lg:!w-56 xl:!w-80",
};

export const DelegateList = () => {
    const seller = useSeller();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedDelegate, setSelectedDelegate] = useState<Delegate | null>(null);
    const [selectedApiKey, setSelectedApiKey] = useState<ApiKeyUser | null>(null);
    const [modalMode, setModalMode] = useState<ModalMode>("delegate");
    const [filters, setFilters] = useState<DelegateListFilterValues>(defaultFilters);

    const handleEdit = (delegate: Delegate) => {
        setSelectedDelegate(delegate);
        setIsModalOpen(true);
    };

    const handleEditApiKey = (apiKey: ApiKeyUser) => {
        setSelectedApiKey(apiKey);
        setIsModalOpen(true);
    };

    const { data: delegates, isLoading: isDelegatesLoading } = useCollection<Delegate>([
        "users",
        seller.id,
        "delegates",
    ]);
    const { data: apiKeys, isLoading: isApiKeysLoading } = useCollection<ApiKeyUser>(["sellers", seller.id, "apiKeys"]);

    if (isDelegatesLoading || isApiKeysLoading) {
        return <Loader />;
    }

    const realUsers = filterDelegatesByFilters(filterRealUsers(delegates), filters);
    const filteredApiKeys = filterApiKeysByFilters(apiKeys, filters);

    const sortedUsers = sortBy(realUsers, "user.name");
    const sortedApiKeys = sortBy(filteredApiKeys, "name");

    const isModalForDelegate = modalMode === "delegate";
    const modalValue = isModalForDelegate ? selectedDelegate : selectedApiKey;

    const getModalTitle = (): string => {
        if (isModalForDelegate) {
            return selectedDelegate ? "Update User" : "Add User";
        }
        return selectedApiKey ? "Update API Key" : "New API Key";
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedDelegate(null);
        setSelectedApiKey(null);
    };

    const handleAddUser = () => {
        setSelectedDelegate(null);
        setSelectedApiKey(null);
        setModalMode("delegate");
        setIsModalOpen(true);
    };

    const handleAddApiKey = () => {
        setSelectedDelegate(null);
        setSelectedApiKey(null);
        setModalMode("apiKey");
        setIsModalOpen(true);
    };

    const hasUserResults = sortedUsers.length > 0;
    const hasApiKeyResults = sortedApiKeys.length > 0;

    return (
        <>
            <HeaderToolBarActions>
                <div className="flex space-x-4">
                    <SecondaryButton
                        icon={<KeyIcon />}
                        data-testid="access-management-page-add-api-key-button"
                        onClick={handleAddApiKey}
                    >
                        Add API Key
                    </SecondaryButton>
                    <Button
                        data-testid="access-management-page-add-user-button"
                        icon={<UserAddIcon />}
                        onClick={handleAddUser}
                    >
                        Add User
                    </Button>
                </div>
            </HeaderToolBarActions>

            <DelegateListFilter filters={filters} onUpdateFilters={setFilters} />

            <ContentLayout isWide className="space-y-0" classNames={{ root: "!mb-0 !py-0" }}>
                <div>
                    <ContentLayout.Section
                        title="Security"
                        description="Configure security preferences for your organization."
                        classNames={sectionClassNames}
                    >
                        <SessionTimeoutSetting />
                    </ContentLayout.Section>

                    <div className="-mx-5 border-t border-gray-light xl:-mx-10" />
                    <ContentLayout.Section
                        title="Users"
                        description="Grant access to users and configure their permissions"
                        classNames={sectionClassNames}
                    >
                        <div className="divide-y divide-gray-light [&>:first-child]:pt-0">
                            {hasUserResults ? (
                                sortedUsers.map((delegate) => (
                                    <DelegateItem
                                        key={delegate.id}
                                        delegate={delegate}
                                        onEdit={(d) => {
                                            setModalMode("delegate");
                                            handleEdit(d);
                                        }}
                                    />
                                ))
                            ) : (
                                <div className="py-6 text-gray-darker">No users found</div>
                            )}
                        </div>
                    </ContentLayout.Section>

                    {hasApiKeyResults && (
                        <>
                            <div className="-mx-5 border-t border-gray-light xl:-mx-10" />
                            <ContentLayout.Section
                                title="API Keys"
                                description="Grant programmatic access to your account"
                                classNames={sectionClassNames}
                            >
                                <div className="divide-y divide-gray-light [&>:first-child]:pt-0">
                                    {sortedApiKeys.map((apiKey) => (
                                        <ApiKeyItem
                                            key={apiKey.id}
                                            apiKey={apiKey}
                                            onEdit={(key) => {
                                                setModalMode("apiKey");
                                                handleEditApiKey(key);
                                            }}
                                        />
                                    ))}
                                </div>
                            </ContentLayout.Section>
                        </>
                    )}
                </div>
            </ContentLayout>

            <DelegateModal
                isOpen={isModalOpen}
                title={getModalTitle()}
                value={modalValue}
                mode={modalMode}
                onClose={handleCloseModal}
            />
        </>
    );
};
